import Bugsnag from '@bugsnag/js'
import { useGoogleReCaptcha } from 'react-google-recaptcha-ultimate'

const getReCaptchaToken = async (
  executeV3: ReturnType<typeof useGoogleReCaptcha>['executeV3'] | null,
) => {
  if (!executeV3) {
    console.warn('Execute reCaptcha not available')
    return
  }

  if (
    !process.env.NEXT_PUBLIC_GOOGLE_CLOUD_PROJECT_ID ||
    !process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY
  ) {
    const error =
      'ReCaptcha environment variables are missing. This indicates a deployment/configuration error.'

    console.error(error)
    throw new Error('ReCaptcha configuration error: missing environment variables')
  }

  return executeV3('submit')
}

/**
 * Non-hook version of recaptcha token generation that can be called at execution time.
 * This replaces the old useReCaptchaToken hook to avoid token expiration issues.
 * @param executeV3 - The executeV3 function from useGoogleReCaptcha hook
 * @param isReCaptchaEnabled - Whether recaptcha is enabled for the campaign
 * @returns Promise<string | undefined> - Fresh recaptcha token
 */
export const getReCaptchaTokenAtExecutionTime = async (
  executeV3: ReturnType<typeof useGoogleReCaptcha>['executeV3'] | null,
  isReCaptchaEnabled: boolean,
): Promise<string | undefined> => {
  // Handle legitimate business case: recaptcha disabled for this campaign
  if (!isReCaptchaEnabled) {
    return undefined
  }

  // Handle technical error: executeV3 should be available when recaptcha is enabled
  if (typeof executeV3 !== 'function') {
    const error =
      'ReCaptcha is enabled but executeV3 function is not available. This indicates a bug in our Google ReCaptcha integration.'

    console.error(error)
    Bugsnag.notify(new Error(error))

    throw new Error('ReCaptcha integration error: executeV3 function not available')
  }

  try {
    return await getReCaptchaToken(executeV3)
  } catch (error) {
    // If it's a configuration error, let it bubble up
    if (error instanceof Error && error.message.includes('configuration error')) {
      throw error
    }

    const log = 'Error getting reCaptcha token at execution time: ' + error
    console.error(log)
    Bugsnag.notify(log)
    return undefined
  }
}
